import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonLoading,
  IonNote,
  IonText,
} from "@ionic/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useState } from "react";

interface BackfillResult {
  success: boolean;
  scanned: number;
  updated: number;
  dryRun: boolean;
}

/**
 * ONE-TIME admin tool: runs backfillArtistPrivacy (functions/src/index.ts),
 * which stamps `privacy: "public"` on artist profile docs that predate the
 * privacy feature. Required before the profile-privacy rule
 * (canReadArtist() in firestore.rules) can be enabled without hiding
 * legacy profiles from the public directory/region pages — see that
 * function's doc comment for the full explanation.
 *
 * Remove this tab (and the Cloud Function it calls) once the backfill has
 * been run against production.
 */
const ArtistPrivacyBackfill: React.FC = () => {
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BackfillResult | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const backfill = httpsCallable<{ dryRun: boolean }, BackfillResult>(
        getFunctions(),
        "backfillArtistPrivacy",
      );
      const res = await backfill({ dryRun });
      setResult(res.data);
    } catch (err: any) {
      setError(err?.message || "Backfill failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle>Artist Privacy Backfill</IonCardTitle>
        <IonCardSubtitle>
          One-time: stamps privacy: "public" on profiles created before the
          privacy feature existed.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <IonItem lines="none">
          <IonCheckbox
            slot="start"
            checked={dryRun}
            onIonChange={(e) => setDryRun(e.detail.checked)}
          />
          <IonLabel>Dry run (preview without writing)</IonLabel>
        </IonItem>

        <div style={{ marginTop: 16 }}>
          <IonButtons>
            <IonButton
              expand="block"
              color={dryRun ? "favorite" : "danger"}
              disabled={loading}
              onClick={run}
            >
              {dryRun ? "Run Dry Run" : "Run Backfill"}
            </IonButton>
          </IonButtons>
        </div>

        {error && (
          <IonItem color="danger" lines="none" style={{ marginTop: 12 }}>
            <IonLabel className="ion-text-wrap">
              <h3>Error</h3>
              <p>{error}</p>
            </IonLabel>
          </IonItem>
        )}

        {result && (
          <IonCard style={{ marginTop: 12 }}>
            <IonCardHeader color={result.dryRun ? "favorite" : "success"}>
              <IonCardTitle>
                {result.dryRun ? "Dry Run Result" : "Backfill Complete"}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>
                  <strong>Profiles scanned:</strong> {result.scanned}
                </p>
                <p>
                  <strong>
                    Profiles {result.dryRun ? "that would be " : ""}updated:
                  </strong>{" "}
                  {result.updated}
                </p>
              </IonText>
              <IonNote>
                {result.updated === 0
                  ? "Nothing left to backfill — safe to deploy the profile-privacy rule."
                  : result.dryRun
                    ? 'Uncheck "Dry run" and run again to write these updates.'
                    : "Done — safe to deploy the profile-privacy rule now."}
              </IonNote>
            </IonCardContent>
          </IonCard>
        )}

        <IonLoading isOpen={loading} message="Running backfill..." />
      </IonCardContent>
    </IonCard>
  );
};

export default ArtistPrivacyBackfill;
